import type { Ref } from 'vue';
import {
    breakpointsTailwind,
    useBreakpoints,
    useMediaQuery,
    useScreenOrientation,
    useWindowFocus,
    useWindowSize,
} from '@vueuse/core';
import { computed } from 'vue';

export type DeviceType = 'mobile' | 'desktop';
export type ScreenOrientation = 'portrait' | 'landscape';
export type BreakpointKey = keyof typeof breakpointsTailwind;

export interface InputMethods {
    mouse: Ref<boolean>
    touch: Ref<boolean>
    hover: Ref<boolean>
}

export function useDeviceContext() {
    const focused = useWindowFocus();
    /* breakpoints
        sm 640px
        md 768px
        lg 1024px
        xl 1280px
        2xl 1536px
    */
    const breakpoints = useBreakpoints(breakpointsTailwind);
    const breakpointsFlat = {
        greater: (bp: BreakpointKey) => breakpoints.greater(bp).value,
        greaterOrEqual: (bp: BreakpointKey) => breakpoints.greaterOrEqual(bp).value,
        smaller: (bp: BreakpointKey) => breakpoints.smaller(bp).value,
        smallerOrEqual: (bp: BreakpointKey) => breakpoints.smallerOrEqual(bp).value,
        inBetween: (bp1: BreakpointKey, bp2: BreakpointKey) => breakpoints.between(bp1, bp2).value,
    };

    const { orientation } = useScreenOrientation();
    const { width, height } = useWindowSize();

    const isMobile = breakpoints.smaller('md');
    const isTablet = breakpoints.between('md', 'lg');
    const tillDesktop = breakpoints.smaller('lg');
    const isDesktop = breakpoints.greaterOrEqual('lg');
    const deviceType = computed<DeviceType>(() => isMobile.value ? 'mobile' : 'desktop');

    const orientationType = computed<ScreenOrientation>(() => {
        if (orientation.value === 'landscape-primary' || orientation.value === 'landscape-secondary') {
            return 'landscape';
        }
        return 'portrait';
    });

    return {
        breakpoints: breakpointsFlat,
        orientation: orientationType,
        width,
        height,

        isMobile,
        isTablet,
        isDesktop,
        tillDesktop,
        deviceType,

        inputMethods: {
            mouse: useMediaQuery('(pointer: fine)'),
            touch: useMediaQuery('(pointer: coarse)'),
            hover: useMediaQuery('(hover: hover)'),
        } satisfies InputMethods,
        focused,
    };
}

export type DeviceContext = ReturnType<typeof useDeviceContext>;
